import React, { useState, useRef, useEffect } from "react";
import { Box, FormControl, Select, MenuItem, Slide } from "@mui/material";
import {
  PlayActiveLightIcon,
  PauseActiveLightIcon,
  CancelIcon,
} from "../../../../assets/icon";
import { SelectChangeEvent } from "@mui/material/Select";

interface TtsMinimalPlayerProps {
  audioUrl: string;
  isPlaying: boolean;
  onPlayPause: () => void;
  onClose: () => void;
  onSpeedChange: (speed: number) => void;
}

export const TtsMinimalPlayer: React.FC<TtsMinimalPlayerProps> = ({
  audioUrl,
  isPlaying,
  onPlayPause,
  onClose,
  onSpeedChange,
}) => {
  const [playbackRate, setPlaybackRate] = useState<number>(1);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsVisible(true);
    return () => setIsVisible(false);
  }, []);

  const handlePlayPause = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onPlayPause();
  };

  const handleClose = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleSpeedChange = (event: SelectChangeEvent<number>) => {
    event.preventDefault();
    event.stopPropagation();
    const newRate = event.target.value as number;
    setPlaybackRate(newRate);
    onSpeedChange(newRate);
  };

  const handleContainerClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <Slide direction="up" in={isVisible} timeout={300}>
      <Box
        ref={containerRef}
        onClick={handleContainerClick}
        sx={{
          position: "fixed",
          bottom: "115px",
          left: { md: "43%", xs: "25%" },
          zIndex: 999,
          display: "flex",
          alignItems: "center",
          gap: 2,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          borderRadius: "12px",
          padding: { xs: "10px", sm: "10px", md: "10px" },
          margin: { xs: "29px 10px", sm: "3px", md: "5px" },
          userSelect: "none",
          pointerEvents: "auto",
          isolation: "isolate",
          contain: "layout style paint",
        }}
      >
        <Box
          onClick={handlePlayPause}
          sx={{ background: "#fff", borderRadius: "25px", cursor: "pointer" }}
        >
          {isPlaying ? (
            <PauseActiveLightIcon style={{ width: 32, height: 32 }} />
          ) : (
            <PlayActiveLightIcon style={{ width: 32, height: 32 }} />
          )}
        </Box>

        <FormControl variant="outlined" size="small">
          <Select
            value={playbackRate}
            onChange={handleSpeedChange}
            sx={{
              width: "65px",
              fontSize: "0.7rem",
              borderRadius: "12px",
              background: "#fff",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#06B6D4" },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#06B6D4",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#06B6D4",
              },
              "& .MuiSvgIcon-root": { color: "#06B6D4" },
              color: "#06B6D4",
            }}
          >
            <MenuItem sx={{ fontSize: "14px" }} value={0.5}>
              0.5x
            </MenuItem>
            <MenuItem sx={{ fontSize: "14px" }} value={1}>
              1x
            </MenuItem>
            <MenuItem sx={{ fontSize: "14px" }} value={1.5}>
              1.5x
            </MenuItem>
            <MenuItem sx={{ fontSize: "14px" }} value={2}>
              2x
            </MenuItem>
          </Select>
        </FormControl>

        <Box onClick={handleClose} sx={{ cursor: "pointer" }}>
          <CancelIcon />
        </Box>
      </Box>
    </Slide>
  );
};

export default TtsMinimalPlayer;
